<template>
    <v-container>
        <v-form ref="form" @submit.prevent="submit" lazy-validation v-model="valid">
            <v-stepper v-model="section" vertical elevation="0">
                <v-stepper-step :complete="section > 1" step="1">
                    Información de Pago
                    <small>Carga el comprobante o Voucher</small>
                </v-stepper-step>

                <v-stepper-content step="1">
                    <v-card color="transparent" elevation="0" class="pb-3">
                        <v-row justify="center">
                            <v-col cols="12" md="10">
                                <v-subheader v-if="loading === false">{{ labels.Qty }}</v-subheader>
                                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-if="loading === true"/>
                                <v-slider
                                    ref="Qty"
                                    v-model="form.Qty"
                                    :tick-labels="['2', '3', '4', '5']"
                                    color="primary"
                                    thumb-color="primary"
                                    track-color="secondary"
                                    thumb-label="always"
                                    :thumb-size="18"
                                    ticks="always"
                                    tick-size="7"
                                    :disabled="loading"
                                    :rules="[rules.required('Qty')]"
                                    :error-messages="errors.Qty"
                                    :min="QtyMin"
                                    :max="QtyMax"
                                    v-else>
                                    <template v-slot:thumb-label>
                                        <v-icon color="secondary" size="12">mdi-check-bold</v-icon>
                                    </template>
                                    <template v-slot:prepend>
                                        <v-icon color="primary" size="32" @click="decrement">mdi-minus-circle</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-icon color="primary" size="32" @click="increment">mdi-plus-circle</v-icon>
                                    </template>
                                </v-slider>
                            </v-col>
                            <v-col cols="12" sm="6" class="py-1">
                                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-if="loading === true"/>
                                <v-select
                                    :label="labels.cSend"
                                    :items="senders"
                                    name="cSend"
                                    v-model="form.cSend"
                                    :error-messages="errors.cSend"
                                    :disabled="loading"
                                    :rules="[rules.req('cSend')]"
                                    @select="clearErrors('cSend')"
                                    prepend-icon="mdi-hand-coin"
                                    filled
                                    v-else>
                                    <template v-slot:selection="{ item }">
                                        <v-flex>
                                            <v-avatar size="28px">
                                                <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                            </v-avatar>
                                            <span class='ml-1'>{{ item.code }}{{ item.bank != null ? " - " + item.bankName : "" }}</span>
                                        </v-flex>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <v-list-item-avatar>
                                            <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title v-html="$vuetify.breakpoint.xsOnly ? item.code : item.code"/>
                                            <v-list-item-subtitle>{{ item.bankName }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="12" sm="6" class="py-1">
                                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-if="loading === true"/>
                                <v-currency-field
                                    :label="labels.amount"
                                    v-model="form.amount"
                                    name="amount"
                                    filled
                                    :error-messages="errors.amount"
                                    :rules="[rules.required('amount'), rules.minus('amount', form.amount, min, 'El'), rules.maxim('amount', form.amount, max, 'El')]"
                                    :disabled="loading"
                                    @input="clearErrors('amount')"
                                    prepend-icon="mdi-cash"
                                    v-bind="currency_send"
                                    v-else/>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-if="loading === true"/>
                                <v-select
                                    :label="labels.Payment"
                                    :items="payments"
                                    name="Payment"
                                    v-model="form.Payment"
                                    :error-messages="errors.Payment"
                                    :disabled="loading"
                                    :rules="[rules.req('Payment')]"
                                    @select="clearErrors('Payment')"
                                    prepend-icon="mdi-chart-donut"
                                    item-text="text"
                                    item-value="value"
                                    filled
                                    v-else>
                                    <template v-slot:selection="{ item }">
                                        <v-flex>
                                            <v-avatar size="28px" color="primary">
                                                <v-icon class="secondary--text">{{ item.icon }}</v-icon>
                                            </v-avatar>
                                            <span class='ml-1'>{{ item.text }}</span>
                                        </v-flex>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <v-list-item-avatar color="primary">
                                            <v-icon class="secondary--text">{{ item.icon }}</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title v-html="item.text"/>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="5" v-if="form.Payment === 'Office'">
                                <p class="text-center mb-0">► Tendrá que realizar el pago en efectivo en las cajas de nuestras oficinas.
                                    <br>► Todas las transferencias en nuestras OFICINAS NO TIENEN COSTO.</p>
                            </v-col>
                            <v-col cols="12" md="10" v-if="form.Payment === 'Wallet'">
                                <v-alert dense text :type="walletCanPay ? 'success' : 'warning'" class="mb-0">
                                    Disponible: {{ walletBalanceText }}. Se descontará {{ Number(form.amount).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2}) }} {{ form.cSend.code }} al enviar.
                                </v-alert>
                            </v-col>
                            <v-col cols="12" sm="6" v-else-if="form.Payment === 'Bank'">
                                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-if="loading === true"/>
                                <v-file-input
                                    :label="labels.Voucher"
                                    v-model="form.Voucher"
                                    name="Voucher"
                                    accept="image/*"
                                    :error-messages="errors.Voucher"
                                    :rules="[rules.req('Voucher')]"
                                    :disabled="loading"
                                    prepend-icon="mdi-camera"
                                    @input="clearErrors('Voucher')"
                                    @click:clear="clearVoucher"
                                    show-size
                                    counter
                                    filled
                                    v-else
                                />
                            </v-col>
                            <v-col cols="12" md="10" v-if="form.Payment === 'Bank'">
                                <p class="text-center error--text mb-0">► Al subir el voucher acepta que esta marcado con el ASUNTO CORRESPONDIENTE.</p>
                            </v-col>
                            <v-col cols="6" sm="5" md="4" lg="3" v-if="imageData !== null">
                                <img :src="imageData" width="100%" alt="voucher" style="border: 1px solid #eee; border-radius: 4px;"/>
                            </v-col>
                        </v-row>
                    </v-card>

                    <v-btn color="primary" @click="next(1)">Continuar</v-btn>
                    <v-btn color="tertiary" dark @click="$router.go(-1)">Cancelar</v-btn>
                </v-stepper-content>

                <v-stepper-step step="2">
                    Beneficiarios
                    <small>Resumen de los Datos</small>
                </v-stepper-step>

                <v-stepper-content step="2">
                    <v-card color="transparent" elevation="0" class="pb-3">
                        <v-row justify="center">
                            <template v-for="(receiver, i) in form.receivers">
                            <v-col cols="12" sm="10" class="text-center" v-if="receiver">
                                <v-simple-table dense>
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th colspan="2">
                                                <h3 class="text-center">
                                                    <strong>Beneficiario {{ i+1 }}</strong>
                                                </h3>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td class="text-right" width="40%">Nombres</td>
                                            <td class="text-left" width="60%">{{ receiver.name }}</td>
                                        </tr>
                                        <tr v-if="receiver.cReceiver.country !== 'BRA' && receiver.cReceiver.country !== 'USA' && receiver.cReceiver.code !== 'USDT'">
                                            <td class="text-right">Documento</td>
                                            <td class="text-left">{{ receiver.td }} - {{ receiver.nd }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-right">Banco</td>
                                            <td class="text-left">{{ receiver.bn }}</td>
                                        </tr>
                                        <tr v-if="receiver.bn !== 'Nuestras Oficinas'">
                                            <td class="text-right">Cuenta</td>
                                            <td class="text-left" style="overflow-wrap: anywhere;">{{ receiver.tc }}<br>{{ (receiver.cReceiver.country === 'VEN' ? receiver.bank + ' ' : '') + receiver.nc }}</td>
                                        </tr>
                                        <tr v-else>
                                            <td class="text-right">Sucursal</td>
                                            <td class="text-left">{{ receiver.br }}</td>
                                        </tr>
                                        <tr v-if="receiver.bn === 'Nuestras Oficinas'">
                                            <td class="text-right">Telefono</td>
                                            <td class="text-left">{{ receiver.phone }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-right">Valor Enviado</td>
                                            <td class="text-left">{{ Number(receiver.vSend).toLocaleString("en-US") }} {{ form.cSend.code }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-right">Tasa de Cambio</td>
                                            <td class="text-left">{{ Number(receiver.fx).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 6}) }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-right">Comisión Destino</td>
                                            <td class="text-left">{{ Number(receiver.feeOut).toLocaleString("en-US") }} {{ receiver.cReceiver.code }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-right">Valor Destino</td>
                                            <td class="text-left">{{ Number(receiver.vReceiver).toLocaleString("en-US") }} {{ receiver.cReceiver.code }}</td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                                <v-btn @click="getEdit(i)" depressed class="my-4" color="primary" style="display:inline-grid;white-space:normal;">
                                    <v-icon>mdi-pencil</v-icon> Corregir
                                </v-btn>
                            </v-col>
                            </template>
                            <template v-for="n in form.Qty">
                                <v-col cols="12" sm="10" class="text-center" v-if="!form.receivers[n-1]">
                                    <v-btn @click="getAdd()" depressed :disabled="(n-1) > form.receivers.length" class="my-4" color="primary" style="display:inline-grid;white-space:normal;">
                                        <v-icon>mdi-account-plus</v-icon> Agregar Destinatario {{ n }}
                                    </v-btn>
                                </v-col>
                            </template>
                        </v-row>
                    </v-card>

                    <v-btn color="success" :loading="loading" :disabled="loading || !valid" @click="submit">Enviar</v-btn>
                    <v-btn color="tertiary" dark @click="back(2)">Atras</v-btn>
                </v-stepper-content>
            </v-stepper>

            <v-dialog v-model="add" persistent transition="dialog-bottom-transition">
                <v-stepper v-model="sectionB" vertical elevation="0">
                    <v-stepper-step :complete="sectionB > 1" step="1">
                        Calcule su Envío
                        <small>Puede modificar el valor depositado o el valor en destino</small>
                    </v-stepper-step>

                    <v-stepper-content step="1">
                        <v-card color="transparent" elevation="0" class="pb-3">
                            <v-row justify="center">
                                <v-col cols="12" class="pt-0 pb-2">
                                    <h3 class="text-center grey--text text--darken-1">TU ENVIAS</h3>
                                </v-col>
                                <v-col cols="6" class="py-1">
                                    <v-currency-field
                                        v-if="!loading && add === true"
                                        :label="labels.Value"
                                        v-model="receiver.amount"
                                        name="amount"
                                        filled
                                        :error-messages="errors.amount"
                                        :rules="[rules.required('Value'), rules.minus('Value', receiver.amount, min/form.Qty, 'El'), rules.maxim('Value', receiver.amount, form.amount-(min/form.Qty), 'El')]"
                                        :disabled="loading"
                                        @input="clearErrors('amount')"
                                        @keyup="receiver.amount > 0 ? getCalculate() : null"
                                        v-bind="currency_send"
                                    />
                                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-else/>
                                </v-col>
                                <v-col cols="6" class="py-1">
                                    <v-select
                                        v-if="!loading && add === true"
                                        :label="labels.Currency"
                                        :items="senders"
                                        name="cSend"
                                        v-model="form.cSend"
                                        :error-messages="errors.cSend"
                                        :disabled="loading"
                                        :rules="[rules.req('Currency')]"
                                        @select="clearErrors('cSend')"
                                        readonly
                                        filled>
                                        <template v-slot:selection="{ item }">
                                            <v-flex>
                                                <v-avatar size="24px">
                                                    <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                                </v-avatar>
                                                <span class='ml-1'>{{ item.code }}{{ item.bank != null ? " - " + item.bankName : "" }}</span>
                                            </v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar>
                                                <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="$vuetify.breakpoint.xsOnly ? item.code : item.code"/>
                                                <v-list-item-subtitle>{{ item.bankName }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </template>
                                    </v-select>
                                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-else/>
                                </v-col>
                            </v-row>
                            <v-row justify="center">
                                <v-col cols="12" class="py-1">
                                    <v-skeleton-loader class="my-4 mx-auto" width="100%" height="60px" type="image" style="border-radius: 5px" v-if="loading === true || quoteRefreshing === true"/>
                                    <v-card class="pa-3 primary--text" style="border-radius: 5px" v-else>
                                    <span>
                                        <strong><v-icon color="primary">mdi-minus-box</v-icon> Costo de Envío:</strong>
                                        {{ receiver.feeIn ? Number(receiver.feeIn).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2}) : 0 }} {{ form.cSend.code }}
                                    </span>
                                        <br>
                                        <span>
                                        <strong><v-icon color="primary">mdi-equal-box</v-icon> Monto a Convertir:</strong>
                                        {{ receiver.vSend ? Number(receiver.vSend).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2}) : 0 }} {{ form.cSend.code }}
                                    </span>
                                        <br>
                                        <span>
                                        <strong><v-icon color="primary">mdi-{{ (receiver.type === 'sell' ? 'division' : 'close') }}-box</v-icon> Tasa de Cambio:</strong>
                                        {{ receiver.fx ? Number(receiver.fx).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 7}) : 0 }}
                                    </span>
                                        <br>
                                        <span>
                                        <strong><v-icon color="primary">mdi-minus-box</v-icon> Costo de Pago:</strong>
                                        {{ receiver.feeOut ? Number(receiver.feeOut).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2}) : 0 }} {{ receiver.cReceiver.code }}
                                    </span>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" class="pt-0 text-center" v-if="!loading && receiver.fx">
                                    <v-chip small label :color="quoteRemaining <= 60 ? 'warning' : 'primary'" class="secondary--text">
                                        <v-icon left small>mdi-timer-sand</v-icon>
                                        Cotización {{ quoteCountdown }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                            <v-row justify="center">
                                <v-col cols="12" class="pt-3 pb-2">
                                    <h3 class="text-center grey--text text--darken-1">TU DESTINATARIO RECIBE</h3>
                                </v-col>
                                <v-col cols="6" class="py-1">
                                    <v-currency-field
                                        v-if="!loading && add === true"
                                        :label="labels.Value"
                                        v-model="receiver.vReceiver"
                                        name="vReceiver"
                                        filled
                                        :error-messages="errors.vReceiver"
                                        :rules="[rules.required('Value')]"
                                        :disabled="loading"
                                        @input="clearErrors('vReceiver')"
                                        @keyup="receiver.vReceiver > 0 ? getCalculate(2) : null"
                                        v-bind="currency_receiver"
                                    />
                                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-else/>
                                </v-col>
                                <v-col cols="6" class="py-1">
                                    <v-select
                                        v-if="!loading && add === true"
                                        :label="labels.Currency"
                                        :items="receivers"
                                        name="cReceiver"
                                        v-model="receiver.cReceiver"
                                        :error-messages="errors.cReceiver"
                                        :disabled="loading"
                                        :rules="[rules.req('Currency')]"
                                        @select="clearErrors('cReceiver')"
                                        filled>
                                        <template v-slot:selection="{ item }">
                                            <v-flex>
                                                <v-avatar size="24px">
                                                    <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                                </v-avatar>
                                                <span class='ml-1'>{{ item.code }}{{ item.bank != null ? " - " + item.bankName : "" }}</span>
                                            </v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar>
                                                <img :src="'/img/country/' + (item.country ?? '000') + '.png'" :alt="item.country"/>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="$vuetify.breakpoint.xsOnly ? item.code : item.code"/>
                                                <v-list-item-subtitle>{{ item.bankName }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </template>
                                    </v-select>
                                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar" v-else/>
                                </v-col>
                            </v-row>
                        </v-card>

                        <v-btn color="primary" @click="next(1)" v-if="!loading && add === true">Continuar</v-btn>
                        <v-skeleton-loader class="my-2 mx-auto" width="100%" type="button" v-else/>
                        <v-btn color="tertiary" dark @click="close()" v-if="!loading && add === true">Cancelar</v-btn>
                        <v-skeleton-loader class="my-2 mx-auto" width="100%" type="button" v-else/>
                    </v-stepper-content>

                    <v-stepper-step :complete="sectionB > 2" step="2">
                        Datos del Destinatario
                        <small>Ingrese la información solicitada</small>
                    </v-stepper-step>

                    <v-stepper-content step="2">
                        <v-card color="transparent" elevation="0" class="pb-3">
                            <v-row justify="center">
                                <v-col :cols="(edit ? '10': '12')" sm="10" lg="9">
                                    <v-autocomplete
                                        v-if="sectionB > 1"
                                        :label="labels.account"
                                        :items="accounts"
                                        name="account"
                                        placeholder="Registrar nueva cuenta"
                                        v-model="receiver.account"
                                        :error-messages="errors.account"
                                        :disabled="loading"
                                        clearable
                                        clear-icon="mdi-close-circle"
                                        :rules="[]"
                                        @select="clearErrors('account')"
                                        prepend-icon="mdi-clipboard-text"
                                        item-text="details"
                                        item-value="id"
                                        filled>
                                        <template v-slot:selection="{ item }">
                                            <v-flex>{{ item.details }}</v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar color="primary">
                                                <v-icon class="secondary--text">mdi-briefcase-account</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="item.name + '<br>' + item.bank_details"/>
                                            </v-list-item-content>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                                <v-col cols="2" sm="3" lg="2" align-self="center" v-if="receiver.account">
                                    <v-btn color="primary" class="px-0" block depressed @click="edit = !edit">
                                        <v-icon>mdi-pencil</v-icon>
                                        <span v-show="$vuetify.breakpoint.smAndUp">Editar</span>
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row justify="center">
                                <v-col cols="12" :sm="receiver.bn === 'Nuestras Oficinas' ? 12 : 6">
                                    <v-text-field
                                        v-if="sectionB > 1"
                                        :label="labels.name"
                                        v-model="receiver.name"
                                        type="text"
                                        name="name"
                                        :error-messages="errors.name"
                                        :rules="[rules.required('name'), rules.min('name', 3, 'El'), rules.max('name', 100, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-account-circle"
                                        @input="clearErrors('name')"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-autocomplete
                                        v-if="sectionB > 1"
                                        :label="labels.bank"
                                        :items="banks"
                                        name="bank"
                                        v-model="receiver.bank"
                                        :error-messages="errors.bank"
                                        :disabled="loading"
                                        :readonly="edit"
                                        :rules="[rules.required('bank')]"
                                        @select="clearErrors('bank')"
                                        prepend-icon="mdi-domain"
                                        item-text="name"
                                        item-value="code"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-if="receiver.bn === 'Nuestras Oficinas'">
                                    <v-autocomplete
                                        v-if="sectionB > 1"
                                        :label="labels.branch"
                                        :items="branches"
                                        name="branch"
                                        v-model="receiver.branch"
                                        :error-messages="errors.branch"
                                        :disabled="loading"
                                        :readonly="edit"
                                        :rules="[rules.required('branch')]"
                                        @select="clearErrors('branch')"
                                        prepend-icon="mdi-store"
                                        item-text="name"
                                        item-value="id"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-if="receiver.cReceiver.country !== 'BRA' && receiver.cReceiver.country !== 'USA' && receiver.cReceiver.code !== 'USDT'">
                                    <v-select
                                        v-if="sectionB > 1"
                                        :label="labels.td"
                                        :items="td"
                                        name="td"
                                        v-model="receiver.td"
                                        :error-messages="errors.td"
                                        :disabled="loading"
                                        :readonly="edit"
                                        :rules="[rules.required('td')]"
                                        @select="clearErrors('td')"
                                        prepend-icon="mdi-clipboard-text"
                                        item-text="name"
                                        item-value="value"
                                        filled>
                                        <template v-slot:selection="{ item }">
                                            <v-flex>
                                                <v-avatar size="28px" color="primary">
                                                    <span class="secondary--text subtitle-2 text-center" style="width: 100%">{{ item.value }}</span>
                                                </v-avatar>
                                                <span class='ml-1'>{{ item.name }}</span>
                                            </v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar color="primary">
                                            <span class="secondary--text subtitle-1 text-center" style="width: 100%">
                                                <strong>{{ item.value }}</strong>
                                            </span>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="item.name"/>
                                            </v-list-item-content>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col cols="12" sm="6" v-if="sectionB > 1 && receiver.country !== 'BRA' && receiver.country !== 'USA' && receiver.cReceiver.code !== 'USDT'">
                                    <v-text-field
                                        v-if="receiver.cReceiver.country === 'CHL' && (receiver.td === 'CI' || receiver.td === 'CE')"
                                        :label="labels.nd"
                                        v-model="receiver.nd"
                                        type="text"
                                        name="nd"
                                        :error-messages="errors.nd"
                                        :rules="[rules.required('nd'), rules.equal('nd', 10, 9, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-ballot"
                                        @input="clearErrors('nd')"
                                        v-mask="rut"
                                        filled
                                    />
                                    <v-text-field
                                        v-if="receiver.td === 'PAS'"
                                        :label="labels.nd"
                                        v-model="receiver.nd"
                                        type="text"
                                        name="nd"
                                        :error-messages="errors.nd"
                                        :rules="[rules.required('nd'), rules.min('nd', 5, 'El'), rules.max('nd', 20, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-ballot"
                                        @input="clearErrors('nd')"
                                        filled
                                    />
                                    <v-text-field
                                        :label="labels.nd"
                                        v-model="receiver.nd"
                                        type="number"
                                        name="nd"
                                        :error-messages="errors.nd"
                                        :rules="[rules.required('nd'), rules.min('nd', 5, 'El'), rules.max('nd', 12, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-ballot"
                                        @input="clearErrors('nd')"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-if="receiver.cReceiver.country !== 'USA' && receiver.bn !== 'Nuestras Oficinas'">
                                    <v-select
                                        v-if="sectionB > 1"
                                        :label="labels.tc"
                                        :items="tc"
                                        name="tc"
                                        v-model="receiver.tc"
                                        :error-messages="errors.tc"
                                        :disabled="loading"
                                        :readonly="edit"
                                        :rules="[rules.required('tc')]"
                                        @select="clearErrors('tc')"
                                        prepend-icon="mdi-wallet"
                                        item-text="name"
                                        item-value="value"
                                        filled>
                                        <template v-slot:selection="{ item }">
                                            <v-flex>
                                                <v-avatar size="28px" color="primary">
                                                    <v-icon class="secondary--text">mdi-account-convert</v-icon>
                                                </v-avatar>
                                                <span class='ml-1'>{{ item.name }}</span>
                                            </v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar color="primary">
                                                <v-icon class="secondary--text">mdi-account-convert</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="item.name"/>
                                            </v-list-item-content>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col cols="12" sm="6" v-if="receiver.cReceiver.country === 'VEN' && receiver.bn !== 'Nuestras Oficinas'">
                                    <v-text-field
                                        v-if="sectionB > 1"
                                        :label="labels.nc"
                                        v-model="receiver.nc"
                                        type="tel"
                                        name="nc"
                                        :prefix="receiver.bank"
                                        :error-messages="errors.nc"
                                        :rules="[rules.required('nc'), rules.minT('nc', 19, 20, 'El'), rules.maxT('nc', 19, 20, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-dialpad"
                                        @input="clearErrors('nc')"
                                        v-mask="mask"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-else-if="receiver.bn !== 'Nuestras Oficinas'">
                                    <v-text-field
                                        v-if="sectionB > 1"
                                        :label="receiver.cReceiver.country === 'BRA' && receiver.tc !== null ? receiver.tc + ' Afiliado a Pix' : (receiver.cReceiver.country === 'USA' ? receiver.tc + ' Zelle' : labels.nc)"
                                        v-model="receiver.nc"
                                        :type="receiver.tc === 'Email' ? 'email' : 'tel'"
                                        name="nc"
                                        :error-messages="errors.nc"
                                        :rules="[rules.required('nc'), rules.min('nc', 5, 'El'), rules.max('nc', 50, 'El')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-dialpad"
                                        @input="clearErrors('nc')"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-else>
                                    <v-text-field
                                        v-if="sectionB > 1"
                                        :label="labels.phone"
                                        v-model="receiver.phone"
                                        type="tel"
                                        name="phone"
                                        :error-messages="errors.phone"
                                        :rules="[rules.required('phone')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-dialpad"
                                        @input="clearErrors('phone')"
                                        filled
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" v-if="receiver.cReceiver.code === 'USDT'">
                                    <v-file-input
                                        v-if="sectionB > 1 && (receiver.account === null || edit === false)"
                                        :label="labels.img"
                                        v-model="receiver.img"
                                        name="img"
                                        accept="image/*"
                                        :error-messages="errors.img"
                                        :rules="[rules.req('img')]"
                                        :disabled="loading"
                                        :readonly="edit"
                                        prepend-icon="mdi-image"
                                        @input="clearErrors('img')"
                                        @click:clear="receiver.img = null"
                                        show-size
                                        filled
                                    />
                                </v-col>
                            </v-row>
                        </v-card>

                        <v-btn color="primary" @click="next(2)">Continuar</v-btn>
                        <v-btn color="tertiary" dark @click="back(2)">Atras</v-btn>
                    </v-stepper-content>

                    <v-stepper-step step="3">
                        Resumen
                        <small>Confirme la Información</small>
                    </v-stepper-step>

                    <v-stepper-content step="3">
                        <v-card color="transparent" elevation="0" class="pb-3">
                            <v-row justify="center">
                                <v-col cols="12" sm="10" class="text-center">
                                    <v-simple-table v-if="loading === false" dense>
                                        <template v-slot:default>
                                            <thead>
                                            <tr>
                                                <th colspan="2">
                                                    <h3 class="text-center">
                                                        <strong>Información de Envío</strong>
                                                    </h3>
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td class="text-right" width="40%">Nombres</td>
                                                <td class="text-left" width="60%">{{ receiver.name }}</td>
                                            </tr>
                                            <tr v-if="receiver.cReceiver.country !== 'BRA' && receiver.cReceiver.country !== 'USA' && receiver.cReceiver.code !== 'USDT'">
                                                <td class="text-right">Documento</td>
                                                <td class="text-left">{{ receiver.td }} - {{ receiver.nd }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right">Banco</td>
                                                <td class="text-left">{{ receiver.bn }}</td>
                                            </tr>
                                            <tr v-if="receiver.bn !== 'Nuestras Oficinas'">
                                                <td class="text-right">Cuenta</td>
                                                <td class="text-left" style="overflow-wrap: anywhere;">{{ receiver.tc }}<br>{{ (receiver.cReceiver.country === 'VEN' ? receiver.bank + ' ' : '') + receiver.nc }}</td>
                                            </tr>
                                            <tr v-else>
                                                <td class="text-right">Sucursal</td>
                                                <td class="text-left">{{ receiver.br }}</td>
                                            </tr>
                                            <tr v-if="receiver.bn === 'Nuestras Oficinas'">
                                                <td class="text-right">Telefono</td>
                                                <td class="text-left">{{ receiver.phone }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right">Valor Enviado</td>
                                                <td class="text-left">{{ Number(receiver.vSend).toLocaleString("en-US") }} {{ form.cSend.code }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right">Tasa de Cambio</td>
                                                <td class="text-left">{{ Number(receiver.fx).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 6}) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right">Comisión Destino</td>
                                                <td class="text-left">{{ Number(receiver.feeOut).toLocaleString("en-US") }} {{ receiver.cReceiver.code }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-right">Valor Destino</td>
                                                <td class="text-left">{{ Number(receiver.vReceiver).toLocaleString("en-US") }} {{ receiver.cReceiver.code }}</td>
                                            </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                    <v-skeleton-loader class="my-2 mx-auto" width="100%" type="table" v-else/>
                                    <v-btn @click="back(3)" depressed :disabled="loading" class="my-4" color="primary" style="display:inline-grid;white-space:normal;">
                                        <v-icon>mdi-pencil</v-icon> Corregir
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card>

                        <v-btn color="success" :loading="loading" :disabled="loading" @click="(b === null ? save() : update(b))">{{ (b === null ? 'Agregar' : 'Actualizar') }}</v-btn>
                        <v-btn color="tertiary" dark :disabled="loading" @click="back(3)">Atras</v-btn>
                    </v-stepper-content>
                </v-stepper>
            </v-dialog>
        </v-form>

        <v-dialog v-model="dynamicConfirmDialog" persistent max-width="420px" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card class="security-fixed-dialog">
                <v-card-title class="security-fixed-header primary secondary--text d-flex align-center" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">mdi-cellphone-lock</v-icon>
                    Confirmar envío
                    <v-spacer/>
                    <v-btn icon color="secondary" :disabled="dynamicConfirmLoading" @click="cancelDynamicConfirmation">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="security-fixed-body pt-4">
                    <div class="text-center mb-4">
                        <v-avatar color="secondary" size="58" class="mb-3">
                            <v-icon color="primary" large>mdi-cellphone-key</v-icon>
                        </v-avatar>
                        <div class="subtitle-1 font-weight-bold primary--text">{{ dynamicConfirmTitle }}</div>
                        <div class="body-2 text--secondary mt-1">
                            Usa la clave de seguridad de tu app Valzum. Si no la tienes activa, puedes recibir una clave en tu correo registrado.
                        </div>
                    </div>
                    <v-alert
                        v-if="dynamicConfirmEmailStatus"
                        dense
                        text
                        type="success"
                        class="mb-4">
                        {{ dynamicConfirmEmailStatus }}
                    </v-alert>
                    <security-code-pad
                        v-model="dynamicConfirmCode"
                        :length="6"
                        :error-messages="dynamicConfirmError"
                        :disabled="dynamicConfirmLoading"
                        autofocus
                        @input="dynamicConfirmCode = onlyDynamicKeyDigits(dynamicConfirmCode)"
                        @complete="resolveDynamicConfirmation"
                        @submit="resolveDynamicConfirmation"/>
                    <v-btn
                        block
                        text
                        color="primary"
                        class="mt-2"
                        :loading="dynamicConfirmEmailLoading"
                        :disabled="dynamicConfirmLoading || dynamicConfirmEmailLoading"
                        @click="sendDynamicKeyEmailConfirmation">
                        <v-icon left>mdi-email-lock</v-icon>
                        {{ dynamicConfirmEmailSent ? 'Enviar otra clave al correo' : 'Enviar clave al correo' }}
                    </v-btn>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="security-fixed-footer">
                    <v-btn text color="error" :disabled="dynamicConfirmLoading" @click="cancelDynamicConfirmation">
                        Cancelar
                    </v-btn>
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        class="secondary--text"
                        :loading="dynamicConfirmLoading"
                        :disabled="dynamicConfirmCode.length !== 6"
                        @click="resolveDynamicConfirmation">
                        Confirmar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from "../../helpers/form";
import {devicePayload} from "../../helpers/device";
import SecurityCodePad from "../../components/SecurityCodePad";
import { mask } from 'vue-the-mask';

export default {
    name: "Multiple",
    components: {SecurityCodePad},
    props: [ 'user' ],
    mixins: [ Form ],
    directives: { mask },
    data: () => ({
        loading: true,
        quoteRefreshing: false,
        quoteRemaining: 0,
        quoteTtlSeconds: 300,
        quoteInterval: null,
        section: 1,
        sectionB: 1,
        labels: {
            cSend: 'Moneda de Envío',
            amount: 'Valor Depositado',
            Value: 'Valor',
            Currency: 'Moneda',
            Payment: 'Forma de Pago',
            Voucher: 'Foto del Voucher',
            Qty: 'Transferencias a Realizar',
            account: 'Cuenta Bancaria',
            name: 'Nombre del Beneficiario',
            td: 'Tipo de Documento',
            nd: 'Número de Documento',
            bank: 'Banco del Beneficiario',
            branch: 'Oficina de Pago',
            tc: 'Tipo de Cuenta',
            nc: 'Número de Cuenta',
            phone: 'Telefono del Beneficiario',
            img: 'Imagen'
        },
        form: {
            cSend: '',
            amount: 0,
            feeIn: 0,
            Payment: null,
            Voucher: null,
            Qty: 2,

            receivers: []
        },
        receiver: {
            amount: 0,
            feeIn: 0,
            vSend: 0,
            type: '',
            fx: 0,
            feeOut: 0,
            vReceiver: 0,
            cReceiver: '',

            account: null,
            name: null,
            td: null,
            nd: null,
            bank: null,
            branch: null,
            bn: null,
            br: null,
            tc: null,
            nc: null,
            phone: null,
            img: null,
        },
        b: null,
        add: false,
        edit: false,
        td: [],
        tc: [],
        rates: [],
        fees: {},
        limits: {},
        senders: [],
        receivers: [],
        payments: [],
        walletBalances: [],
        accounts: [],
        banks: [],
        branches: [],
        offices: [],
        officeCount: 0,
        min: null,
        max: null,
        imageData: null,
        dynamicConfirmDialog: false,
        dynamicConfirmTitle: 'Confirma esta remesa',
        dynamicConfirmCode: '',
        dynamicConfirmError: '',
        dynamicConfirmLoading: false,
        dynamicConfirmEmailLoading: false,
        dynamicConfirmEmailSent: false,
        dynamicConfirmEmailStatus: '',
        dynamicConfirmChallengeId: '',
        dynamicConfirmPurpose: '',
        dynamicConfirmResolver: null,
        currency_send: {
            locale: 'en-US',
            decimalLength: 2,
            autoDecimalMode: false,
            min: null,
            max: null,
            defaultValue: 0,
            valueAsInteger: false,
            allowNegative: false
        },
        currency_receiver: {
            locale: 'en-US',
            decimalLength: 2,
            autoDecimalMode: false,
            min: null,
            max: null,
            defaultValue: 0,
            valueAsInteger: false,
            allowNegative: false
        },
        mask: '#### #### #### ####',
        rut: '########-X',
        QtyMin: 2,
        QtyMax: 5,
    }),
    mounted() {
        this.getData();
        this.getWallet();
    },
    watch: {
        'form.Qty'() {
            this.getParams();
        },
        'form.cSend'() {
            this.getParams();
            this.getReceivers();
            this.getPayments(this.form.cSend.country)
        },
        'form.Payment'() {
            if (this.form.Payment) {
                if (this.form.Payment !== 'Bank') {
                    this.form.Voucher = null;
                    this.imageData = null;
                }
            }
        },
        'form.Voucher'() {
            if (this.form.Voucher) {
                this.getVoucher();
            }
        },
        'receiver.cReceiver'() {
            this.getCalculate();
            this.getMyAccounts(this.receiver.cReceiver.country);
            this.getBanks(this.receiver.cReceiver.country);
            this.getTypeDoc(this.receiver.cReceiver.country);
            this.getTypeAcc(this.receiver.cReceiver.country);
            if (this.receiver.cReceiver.country === 'USA') {
                this.receiver.tc = 'Email';
            } else if (this.receiver.cReceiver.code === 'USDT') {
                this.receiver.tc = 'TRC20';
            } else {
                this.receiver.tc = null;
            }
        },
        'receiver.account'() {
            if (this.receiver.account) {
                this.account();
                this.edit = true;
            } else {
                this.receiver.account = null;
                this.receiver.name = null;
                this.receiver.td = null;
                this.receiver.nd = null;
                this.receiver.bank = null;
                this.receiver.branch = null;
                this.receiver.tc = null;
                this.receiver.nc = null;
                this.receiver.phone = null;
                this.edit = false;
                this.$refs.form.resetValidation();
            }
        },
        'receiver.nd'() {
            if (this.receiver.td === 'PAS') {
                this.receiver.nd = (this.receiver.nd).toUpperCase();
            }
        },
        'receiver.bank'() {
            if (this.receiver.bank) {
                this.getBank();
            } else {
                this.receiver.bn = null;
            }
        },
        'receiver.branch'() {
            if (this.receiver.branch) {
                this.getBranch();
            } else {
                this.receiver.br = null;
            }
        }
    },
    computed: {
        'form.feeIn'() {
            let v = 0;
            for (let i = 0; i < this.form.Qty; i++) {
                v = v + this.form.receivers[i].feeIn;
            }

            return parseFloat(v);
        },
        balance() {
            const amount = this.form.amount;
            let v = 0;
            for (let i = 0; i < this.form.Qty; i++) {
                v = v + this.form.receivers[i].amount;
            }

            return parseFloat(amount - v);
        },
        quoteCountdown() {
            const minutes = Math.floor(this.quoteRemaining / 60).toString().padStart(2, '0');
            const seconds = (this.quoteRemaining % 60).toString().padStart(2, '0');

            return minutes + ':' + seconds;
        },
        selectedWalletBalance() {
            if (!this.form.cSend || !this.form.cSend.code) {
                return null;
            }

            return this.walletBalances.find(item => item.currency === this.form.cSend.code) || null;
        },
        walletBalanceAmount() {
            return this.selectedWalletBalance ? parseFloat(this.selectedWalletBalance.amount) : 0;
        },
        walletBalanceText() {
            if (this.selectedWalletBalance && this.selectedWalletBalance.formatted) {
                return this.selectedWalletBalance.formatted;
            }

            return Number(this.walletBalanceAmount).toLocaleString("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 2}) + ' ' + (this.form.cSend.code || '');
        },
        walletCanPay() {
            return this.form.Payment === 'Wallet'
                && parseFloat(this.form.amount) > 0
                && this.walletBalanceAmount + 0.0001 >= parseFloat(this.form.amount);
        },
        isNativeApp() {
            const payload = this.devicePayload();
            const device = String(payload.device || '').toLowerCase();

            return payload.name === 'Valzum'
                && ['android', 'ios', 'iphone', 'ipad'].indexOf(device) !== -1
                && !!(payload.device_uid || payload.ios_id || payload.fcm_token);
        },
        browserDynamicKeyRequired() {
            return this.user && this.user.role === 'Customer' && !this.isNativeApp;
        }
    },
    methods: {
        devicePayload() {
            return devicePayload();
        },
        sensitivePayload(title, purpose) {
            const payload = {
                device_info: this.devicePayload()
            };

            if (!this.browserDynamicKeyRequired) {
                return Promise.resolve(payload);
            }

            return this.requestDynamicKeyConfirmation(title, purpose).then(security => Object.assign(payload, {
                dynamic_key: security.code,
                dynamic_key_challenge_id: security.challenge_id || null
            }));
        },
        requestDynamicKeyConfirmation(title, purpose) {
            return new Promise((resolve, reject) => {
                this.dynamicConfirmTitle = title || 'Confirma esta remesa';
                this.dynamicConfirmCode = '';
                this.dynamicConfirmError = '';
                this.dynamicConfirmLoading = false;
                this.dynamicConfirmEmailLoading = false;
                this.dynamicConfirmEmailSent = false;
                this.dynamicConfirmEmailStatus = '';
                this.dynamicConfirmChallengeId = '';
                this.dynamicConfirmPurpose = purpose || 'remittance_wallet_payment';
                this.dynamicConfirmResolver = {resolve, reject};
                this.dynamicConfirmDialog = true;
            });
        },
        resolveDynamicConfirmation() {
            this.dynamicConfirmCode = this.onlyDynamicKeyDigits(this.dynamicConfirmCode);

            if (this.dynamicConfirmCode.length !== 6 || !this.dynamicConfirmResolver) {
                this.dynamicConfirmError = 'Ingresa los 6 numeros de la clave.';
                return;
            }

            const resolver = this.dynamicConfirmResolver;
            const challengeId = this.dynamicConfirmChallengeId;
            this.dynamicConfirmResolver = null;
            this.dynamicConfirmDialog = false;
            resolver.resolve({
                code: this.dynamicConfirmCode,
                challenge_id: challengeId
            });
        },
        sendDynamicKeyEmailConfirmation() {
            if (this.dynamicConfirmEmailLoading || !this.dynamicConfirmResolver) {
                return;
            }

            this.dynamicConfirmEmailLoading = true;
            this.dynamicConfirmError = '';
            axios.post('/api/security/dynamic-key/email', {
                purpose: this.dynamicConfirmPurpose || 'remittance_wallet_payment',
                device_info: this.devicePayload()
            }).then(res => {
                this.dynamicConfirmChallengeId = res.data.challenge_id || '';
                this.dynamicConfirmEmailSent = true;
                this.dynamicConfirmEmailStatus = res.data.status || 'Enviamos la clave al correo registrado.';
            }).catch(err => {
                this.dynamicConfirmError = this.errorMessage(err, 'No fue posible enviar la clave al correo.');
            }).then(() => {
                this.dynamicConfirmEmailLoading = false;
            });
        },
        cancelDynamicConfirmation() {
            if (this.dynamicConfirmResolver) {
                this.dynamicConfirmResolver.reject(new Error('cancelled'));
            }

            this.dynamicConfirmResolver = null;
            this.dynamicConfirmDialog = false;
            this.dynamicConfirmCode = '';
            this.dynamicConfirmError = '';
            this.dynamicConfirmLoading = false;
            this.dynamicConfirmEmailLoading = false;
            this.dynamicConfirmEmailSent = false;
            this.dynamicConfirmEmailStatus = '';
            this.dynamicConfirmChallengeId = '';
            this.dynamicConfirmPurpose = '';
        },
        onlyDynamicKeyDigits(value) {
            return String(value || '').replace(/\D/g, '').slice(0, 6);
        },
        appendSecurityPayload(formData, payload) {
            const deviceInfo = payload && payload.device_info ? payload.device_info : this.devicePayload();
            Object.keys(deviceInfo).forEach(key => {
                if (deviceInfo[key] !== null && deviceInfo[key] !== undefined) {
                    formData.append('device_info[' + key + ']', deviceInfo[key]);
                }
            });

            if (payload && payload.dynamic_key) {
                formData.append('dynamic_key', payload.dynamic_key);
            }

            if (payload && payload.dynamic_key_challenge_id) {
                formData.append('dynamic_key_challenge_id', payload.dynamic_key_challenge_id);
            }
        },
        errorMessage(err, fallback) {
            if (err && err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            const errors = err && err.response && err.response.data ? err.response.data.errors : null;
            const firstError = this.firstValidationError(errors);
            if (firstError) {
                return firstError;
            }

            if (err && err.response && err.response.data && err.response.data.message) {
                return err.response.data.message;
            }

            return fallback;
        },
        firstValidationError(errors) {
            if (!errors) {
                return '';
            }

            const keys = Object.keys(errors);
            for (let i = 0; i < keys.length; i += 1) {
                const messages = errors[keys[i]];
                if (Array.isArray(messages) && messages.length) {
                    return messages[0];
                }

                if (typeof messages === 'string' && messages) {
                    return messages;
                }
            }

            return '';
        },
        decrement () {
            if (this.form.Qty <= this.QtyMax && this.form.Qty > this.QtyMin) {
                this.form.Qty--;
            }
        },
        increment () {
            if (this.form.Qty < this.QtyMax && this.form.Qty >= this.QtyMin) {
                this.form.Qty++;
            }
        },
        back(n) {
            if (this.add) {
                this.sectionB = n - 1;
            } else {
                this.section = n - 1;
            }
        },
        next(n) {
            if (this.add && !this.ensureFreshQuote()) {
                return;
            }

            if (!this.add && n === 1 && this.form.Payment === 'Wallet' && !this.walletCanPay) {
                this.$toast.warning('Saldo wallet insuficiente para pagar esta remesa.', {icon: 'mdi-wallet-alert'});
                return;
            }

            if (this.$refs.form.validate()) {
                if (this.add) {
                    this.sectionB = n + 1;
                } else {
                    this.section = n + 1;
                }
            }
        },
        getData() {
            clearTimeout(this.timeout);
            axios.get('/api/currencies/all').then((res) => {
                this.rates = res.data.rates;
                this.fees = res.data.fees;
                this.limits = res.data.limits;
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.getSenders();
                this.loading = false;
                this.quoteRefreshing = false;
            });
            this.timeout = setTimeout(this.getData, 1200000);
        },
        getWallet() {
            axios.get('/api/wallet').then((res) => {
                this.walletBalances = res.data.balances || [];
                this.buildPaymentOptions();
            }).catch(() => {
                this.walletBalances = [];
                this.buildPaymentOptions();
            });
        },
        refreshQuote() {
            if (this.loading || this.quoteRefreshing) {
                return;
            }

            this.quoteRefreshing = true;
            this.getData();
        },
        startQuoteTimer() {
            this.stopQuoteTimer();
            this.quoteRemaining = this.quoteTtlSeconds;
            this.quoteInterval = setInterval(this.tickQuote, 1000);
        },
        stopQuoteTimer() {
            if (this.quoteInterval) {
                clearInterval(this.quoteInterval);
                this.quoteInterval = null;
            }
        },
        tickQuote() {
            if (this.quoteRemaining > 0) {
                this.quoteRemaining--;
            }

            if (this.quoteRemaining <= 0) {
                this.stopQuoteTimer();
                this.refreshQuote();
            }
        },
        ensureFreshQuote() {
            if (this.receiver.fx && this.quoteRemaining <= 0) {
                this.$toast.warning('La cotización venció y se está actualizando.', {icon: 'mdi-timer-sand'});
                this.refreshQuote();
                return false;
            }

            return true;
        },
        getAdd() {
            this.receiver.amount = this.min / this.form.Qty;
            this.getCalculate();
            this.add = true;
        },
        save() {
            if (!this.ensureFreshQuote()) {
                return;
            }

            this.loading = true;
            (this.form.receivers).push(this.receiver);
            this.close();
        },
        getEdit(n) {
            this.receiver = this.form.receivers[n];
            this.add = true;
            this.b = n;
        },
        update(n) {
            if (!this.ensureFreshQuote()) {
                return;
            }

            this.loading = true;
            this.form.receivers[n] = this.receiver;
            this.close();
        },
        close() {
            this.$refs.form.resetValidation();
            this.add = false;
            this.edit = false;
            this.receiver = {
                amount: 0,
                feeIn: 0,
                vSend: 0,
                type: '',
                fx: 0,
                feeOut: 0,
                vReceiver: 0,
                cReceiver: '',

                account: null,
                name: null,
                td: null,
                nd: null,
                bank: null,
                branch: null,
                bn: null,
                br: null,
                tc: null,
                nc: null,
                phone: null,
                img: null,
            };
            this.getReceivers();
            this.b = null;
            this.sectionB = 1;
            this.loading = false;
        },
        getSenders() {
            const rates = this.rates;
            const sender = this.form.cSend;
            let senders = [];
            for (let i = 0; i < rates.length; i++) {
                if (rates[i].country_send === this.user.country || rates[i].country_send === null) {
                    const a = {
                        country: rates[i].country_send,
                        code: rates[i].currency_send,
                        digits: rates[i].currency_send_dd,
                        bank: rates[i].bank_send,
                        bankName: rates[i].bank_send_name
                    }
                    let v = (JSON.stringify(senders)).includes(JSON.stringify(a));
                    if (v === false) {
                        senders.push(a);
                    }
                }
            }
            this.senders = senders;
            if (sender.length === 0) {
                this.form.cSend = senders[0];
            }
            this.getParams();
        },
        getParams() {
            let limits = this.limits;
            let min = limits['min'];
            let max = limits['max'];
            let currency = this.form.cSend.code;

            if (min) {
                for (let i = 0; i < min.length; i++) {
                    if (min[i].data.currency === currency) {
                        this.min = parseInt(min[i].data.value) * this.form.Qty;
                    }
                }
            }

            if (max) {
                for (let i = 0; i < max.length; i++) {
                    if (max[i].data.currency === currency) {
                        this.max = parseInt(max[i].data.value) * this.form.Qty;
                    }
                }
            }
        },
        getReceivers() {
            const rates = this.rates;
            const sender = this.form.cSend;
            const receiver = this.receiver.cReceiver;
            let receivers = [];
            for (let i = 0; i < rates.length; i++) {
                if (sender.country === rates[i].country_send &&
                    sender.code === rates[i].currency_send &&
                    sender.bank === rates[i].bank_send) {
                    const a = {
                        country: rates[i].country_receiver,
                        code: rates[i].currency_receiver,
                        digits: rates[i].currency_receiver_dd,
                        bank: rates[i].bank_receiver,
                        bankName: rates[i].bank_receiver_name
                    }
                    let v = (JSON.stringify(receivers)).includes(JSON.stringify(a));
                    if (v === false) {
                        receivers.push(a);
                    }
                }
            }
            this.receivers = receivers;
            if (receiver.length === 0 || sender === receiver) {
                this.receiver.cReceiver = receivers[0];
            }
            this.getCalculate();
        },
        getCalculate(n) {
            if (!this.form.cSend || !this.form.cSend.code || !this.receiver.cReceiver || !this.receiver.cReceiver.code) {
                return;
            }

            let feeIn = this.receiver.feeIn;
            let feeOut = this.receiver.feeOut;
            let type = null;
            let rate = 0;
            let value = this.receiver.amount;
            let send = 0;
            let result = this.receiver.vReceiver;
            const rates = this.rates;
            const fees = this.fees;
            const sender = this.form.cSend;
            const receiver = this.receiver.cReceiver;
            for (let i = 0; i < rates.length; i++) {
                if (sender.country === rates[i].country_send &&
                    sender.code === rates[i].currency_send &&
                    sender.bank === rates[i].bank_send &&
                    receiver.country === rates[i].country_receiver &&
                    receiver.code === rates[i].currency_receiver &&
                    receiver.bank === rates[i].bank_receiver) {
                    type = rates[i].type;
                    rate = rates[i].value;
                }
            }
            if (n === 2) {
                feeOut = 0;
                let b1 = false;
                let b2 = false;
                let b3 = false;
                for (let i = 0; i < fees.length; i++) {
                    if (fees[i].category === 'Out' && parseFloat(fees[i].value) > 0) {
                        if (result >= fees[i].range_min && result <= fees[i].range_max) {
                            if (fees[i].currency === receiver.code || (b1 === false && fees[i].currency === null)) {
                                if (fees[i].currency === receiver.code) {
                                    b1 = true;
                                }
                                if (fees[i].country_receiver === receiver.country || (b2 === false && fees[i].country_receiver === null)) {
                                    if (fees[i].country_receiver === receiver.country) {
                                        b2 = true;
                                    }
                                    if (fees[i].country_send === sender.country || (b3 === false && fees[i].country_send === null)) {
                                        if (fees[i].country_send === sender.country) {
                                            b3 = true;
                                        }
                                        if (fees[i].type === '%' && fees[i].value > 0) {
                                            feeOut = (parseFloat(result) * (1 + (parseFloat(fees[i].value) / 100))) - parseFloat(result);
                                        } else if (fees[i].type === '$') {
                                            feeOut = parseFloat(fees[i].value);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                result = parseFloat(result) + feeOut;
                send = type === 'sell' ? result * parseFloat(rate) : result / parseFloat(rate);
                feeIn = 0;
                let c1 = false;
                let c2 = false;
                let c3 = false;
                for (let i = 0; i < fees.length; i++) {
                    if (fees[i].category === 'In' && parseFloat(fees[i].value) > 0) {
                        if (send >= fees[i].range_min && send <= fees[i].range_max) {
                            if (fees[i].currency === sender.code || (c1 === false && fees[i].currency === null)) {
                                if (fees[i].currency === sender.code) {
                                    c1 = true;
                                }
                                if (fees[i].country_send === sender.country || (c2 === false && fees[i].country_send === null)) {
                                    if (fees[i].country_send === sender.country) {
                                        c2 = true;
                                    }
                                    if (fees[i].country_receiver === receiver.country || (c3 === false && fees[i].country_receiver === null)) {
                                        if (fees[i].country_receiver === receiver.country) {
                                            c3 = true;
                                        }
                                        if (fees[i].type === '%' && parseFloat(fees[i].value) > 0) {
                                            feeIn = (parseFloat(send) * (1 + (parseFloat(fees[i].value) / 100))) - parseFloat(send);
                                        } else if (fees[i].type === '$') {
                                            feeIn = parseFloat(fees[i].value);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                this.receiver.amount = parseFloat(send) + feeIn;
            } else {
                feeIn = 0;
                let c1 = false;
                let c2 = false;
                let c3 = false;
                for (let i = 0; i < fees.length; i++) {
                    if (fees[i].category === 'In' && parseFloat(fees[i].value) > 0) {
                        if (value >= fees[i].range_min && value <= fees[i].range_max) {
                            if (fees[i].currency === sender.code || (c1 === false && fees[i].currency === null)) {
                                if (fees[i].currency === sender.code) {
                                    c1 = true;
                                }
                                if (fees[i].country_send === sender.country || (c2 === false && fees[i].country_send === null)) {
                                    if (fees[i].country_send === sender.country) {
                                        c2 = true;
                                    }
                                    if (fees[i].country_receiver === receiver.country || (c3 === false && fees[i].country_receiver === null)) {
                                        if (fees[i].country_receiver === receiver.country) {
                                            c3 = true;
                                        }
                                        if (fees[i].type === '%') {
                                            feeIn = parseFloat(value) - (parseFloat(value) / (1 + (parseFloat(fees[i].value) / 100)));
                                        } else if (fees[i].type === '$') {
                                            feeIn = parseFloat(fees[i].value);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                send = parseFloat(value) - feeIn;
                result = type === 'sell' ? send / parseFloat(rate) : send * parseFloat(rate);
                feeOut = 0;
                let b1 = false;
                let b2 = false;
                let b3 = false;
                for (let i = 0; i < fees.length; i++) {
                    if (fees[i].category === 'Out' && parseFloat(fees[i].value) > 0) {
                        if (result >= fees[i].range_min && result <= fees[i].range_max) {
                            if (fees[i].currency === receiver.code || (b1 === false && fees[i].currency === null)) {
                                if (fees[i].currency === receiver.code) {
                                    b1 = true;
                                }
                                if (fees[i].country_receiver === receiver.country || (b2 === false && fees[i].country_receiver === null)) {
                                    if (fees[i].country_receiver === receiver.country) {
                                        b2 = true;
                                    }
                                    if (fees[i].country_send === sender.country || (b3 === false && fees[i].country_send === null)) {
                                        if (fees[i].country_send === sender.country) {
                                            b3 = true;
                                        }
                                        if (fees[i].type === '%' && fees[i].value > 0) {
                                            feeOut = parseFloat(result) - (parseFloat(result) / (1 + (parseFloat(fees[i].value) / 100)));
                                        } else if (fees[i].type === '$') {
                                            feeOut = parseFloat(fees[i].value);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                this.receiver.vReceiver = parseFloat(result) - feeOut;
            }
            this.receiver.feeIn = feeIn.toFixed(sender.digits);
            this.receiver.vSend = send;
            this.receiver.type = type;
            this.receiver.fx = rate;
            this.receiver.feeOut = feeOut.toFixed(receiver.digits);

            if (rate > 0 && parseFloat(this.receiver.amount) > 0 && parseFloat(this.receiver.vReceiver) > 0) {
                this.startQuoteTimer();
            }
        },
        getTypeDoc(c) {
            if (c === 'MEX' || c === 'USA') {
                this.td = [
                    { name: 'Licencia de Conducir', value: 'LIC' },
                    { name: 'Pasaporte', value: 'PAS' }
                ]
            } else if (c === 'PER') {
                this.td = [
                    { name: 'Documento Nacional de Identidad', value: 'DNI' },
                    { name: 'Carnet de Extranjeria', value: 'CE' },
                    { name: 'Permiso Temporal de Permanencia', value: 'PTP' },
                    { name: 'Pasaporte', value: 'PAS' }
                ]
            } else if (c === 'COL') {
                this.td = [
                    { name: 'Cedula de Ciudadania', value: 'CC' },
                    { name: 'Cedula de Extranjeria', value: 'CE' },
                    { name: 'Número de Identificación Tributaria', value: 'NIT' },
                    { name: 'Permiso por Protección Temporal', value: 'PPT' },
                    { name: 'Pasaporte', value: 'PAS' }
                ]
            } else if (c === 'VEN') {
                this.td = [
                    { name: 'Cedula de Identidad', value: 'V' },
                    { name: 'Carnet de Extranjeria', value: 'E' },
                    { name: 'RIF', value: 'J' },
                    { name: 'Gubernamental', value: 'G' },
                    { name: 'Firma Personal', value: 'M' },
                    { name: 'Pasaporte', value: 'PAS' }
                ]
            } else if (c === 'CHL') {
                this.td = [
                    {name: 'Rol Único Tributario', value: 'RUT'},
                    {name: 'Rol Único Nacional', value: 'RUN'},
                    {name: 'Cedula de Identidad', value: 'CI'},
                    {name: 'Cedula de Extranjeria', value: 'CE'},
                    {name: 'Licencia de Conducir', value: 'LIC'},
                    {name: 'Pasaporte', value: 'PAS'}
                ]
            } else {
                this.td = [
                    { name: 'Cedula de Identidad', value: 'CI' },
                    { name: 'Cedula de Extranjeria', value: 'CE' },
                    { name: 'Licencia de Conducir', value: 'LIC' },
                    { name: 'Pasaporte', value: 'PAS' }
                ]
            }
        },
        getTypeAcc(c) {
            if (c === 'CHL') {
                this.tc = [
                    { name: 'Cuenta de Ahorros', value: 'Ahorros' },
                    { name: 'Cuenta Corriente', value: 'Corriente' },
                    { name: 'Cuenta Vista', value: 'Vista' },
                    { name: 'Cuenta RUT', value: 'RUT' }
                ]
            } else if (c === 'BRA') {
                this.tc = [
                    { name: 'Celular Afiliado a Pix', value: 'Celular' },
                    { name: 'Número de Documento', value: 'CPF/CNPJ' },
                    { name: 'Email Afiliado a Pix', value: 'Email' },
                    { name: 'Clave Aleatoria de Pix', value: 'Clave' }
                ]
            } else if (c === 'null' || c === null) {
                this.tc = [
                    { name: 'Red Tron (TRC20)', value: 'TRC20' },
                ]
            } else {
                this.tc = [
                    { name: 'Cuenta de Ahorros', value: 'Ahorros' },
                    { name: 'Cuenta Corriente', value: 'Corriente' }
                ]
            }
        },
        getPayments() {
            axios.get('/api/offices').then((res) => {
                this.offices = res.data.offices;
                this.officeCount = res.data.c || 0;
                this.buildPaymentOptions();
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            });
        },
        buildPaymentOptions() {
            const selected = this.form.Payment;
            const payments = [
                { text: 'Deposito/Transferencia', value: 'Bank', icon: 'mdi-bank-transfer' }
            ];

            payments.push({
                text: 'Saldo Wallet (' + this.walletBalanceText + ')',
                value: 'Wallet',
                icon: 'mdi-wallet'
            });

            if (this.officeCount > 0) {
                payments.push({ text: 'Efectivo', value: 'Office', icon: 'mdi-currency-usd' });
            }

            this.payments = payments;
            this.form.Payment = payments.find(item => item.value === selected) ? selected : payments[0].value;
        },
        getVoucher() {
            const input = this.form.Voucher;

            if (input) {
                const reader = new FileReader;
                reader.onload = e => {
                    this.imageData = e.target.result;
                };
                reader.readAsDataURL(input);
            }
        },
        clearVoucher() {
            this.form.Voucher = null;
            this.imageData = null;
        },
        getMyAccounts(country) {
            if (country === 'null' || country === null) {
                country = '000';
            }
            axios.get('/api/receivers/v/' + country).then((res) => {
                let accounts = res.data.items;
                let items = [];

                if (this.receiver.cReceiver.bank) {
                    for (let i = 0; i < accounts.length; i++) {
                        if (accounts[i].bank === this.receiver.cReceiver.bank && (accounts[i].currency === this.receiver.cReceiver.code || accounts[i].currency === null)) {
                            items.push(accounts[i]);
                        }
                    }
                } else {
                    for (let i = 0; i < accounts.length; i++) {
                        if (accounts[i].currency === this.receiver.cReceiver.code || accounts[i].currency === null) {
                            items.push(accounts[i]);
                        }
                    }
                }
                this.accounts = items;
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            });
        },
        account() {
            const accounts = this.accounts;

            for (let i = 0; i < accounts.length; i++) {
                if (accounts[i].id === this.receiver.account) {
                    this.receiver.name = accounts[i].name;
                    this.receiver.td = accounts[i].td;
                    this.receiver.nd = accounts[i].nd;
                    this.receiver.bank = accounts[i].bank_code;
                    this.receiver.branch = accounts[i].branch;
                    this.receiver.bn = accounts[i].bank_name;
                    this.receiver.br = accounts[i].branch_name;
                    this.receiver.tc = accounts[i].tc;
                    this.receiver.nc = this.receiver.cReceiver.country === 'VEN' && accounts[i].nc ? (accounts[i].nc).replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim() : accounts[i].nc;
                    this.receiver.phone = accounts[i].phone;
                }
            }
        },
        getBanks(country) {
            if (country === 'null' || country === null) {
                country = '000';
            }
            axios.get('/api/banks/' + country).then((res) => {
                if (this.receiver.cReceiver.bank !== null) {
                    let banks = res.data.banks;
                    let items = [];

                    for (let i = 0; i < banks.length; i++) {
                        if (banks[i].code === this.receiver.cReceiver.bank) {
                            items.push(banks[i]);
                        }
                    }
                    this.banks = items;
                } else {
                    this.banks = res.data.banks;
                }
                this.branches = res.data.branches;
                if (!this.receiver.bank) {
                    this.receiver.bank = (this.banks)[0].code;
                }
            });
        },
        getBank() {
            const banks = this.banks;

            for (let i = 0; i < banks.length; i++) {
                if (banks[i].code === this.receiver.bank) {
                    if (banks[i].name === 'Nuestras Oficinas') {
                        this.receiver.tc = null;
                        this.receiver.nc = null;
                    } else {
                        this.receiver.branch = null;
                        this.receiver.phone = null;
                    }
                    this.receiver.bn = banks[i].name;
                }
            }
        },
        getBranch() {
            const branches = this.branches;

            for (let i = 0; i < branches.length; i++) {
                if (branches[i].code === this.receiver.branch) {
                    this.receiver.br = branches[i].name;
                }
            }
        },
        async submit() {
            if (this.balance === 0) {
                if (this.$refs.form.validate()) {
                    if (this.form.Payment === 'Wallet' && !this.walletCanPay) {
                        this.$toast.warning('Saldo wallet insuficiente para pagar esta remesa.', {icon: 'mdi-wallet-alert'});
                        return;
                    }

                    let securityPayload = {
                        device_info: this.devicePayload()
                    };
                    try {
                        if (this.form.Payment === 'Wallet') {
                            securityPayload = await this.sensitivePayload('Confirma el pago de la remesa', 'remittance_wallet_payment');
                        }
                    } catch (error) {
                        return;
                    }

                    this.loading = true;

                    let formData = new FormData();

                    formData.append('country', this.form.cSend.country);
                    formData.append('payment', this.form.Payment);
                    formData.append('voucher', this.form.Voucher);
                    formData.append('amount', this.form.amount);
                    formData.append('currency', this.form.cSend.code);
                    formData.append('Qty', this.form.Qty);
                    formData.append('fee', this.form.feeIn);

                    formData.append('account_1', this.form.receivers[0].account ? this.form.receivers[0].account : 0);
                    formData.append('name_1', this.form.receivers[0].name);
                    formData.append('td_1', this.form.receivers[0].td);
                    formData.append('nd_1', this.form.receivers[0].nd);
                    formData.append('bank_1', this.form.receivers[0].bank);
                    formData.append('branch_1', this.form.receivers[0].branch);
                    formData.append('tc_1', this.form.receivers[0].tc);
                    formData.append('nc_1', this.form.receivers[0].nc ? (this.form.receivers[0].nc).replace(/ /g, '') : null);
                    formData.append('phone_1', this.form.receivers[0].phone);
                    formData.append('img_1', this.form.receivers[0].img);
                    formData.append('amount_1', this.form.receivers[0].vSend);
                    formData.append('quotes_1', JSON.stringify(this.form.receivers[0]));
                    formData.append('fee_1', this.form.receivers[0].feeOut);
                    formData.append('fx_1', this.form.receivers[0].fx);
                    formData.append('convert_1', this.form.receivers[0].vReceiver);
                    formData.append('cConvert_1', this.form.receivers[0].cReceiver.code);
                    formData.append('destiny_1', this.form.receivers[0].cReceiver.country);

                    if (this.form.Qty > 1) {
                        formData.append('account_2', this.form.receivers[1].account ? this.form.receivers[1].account : 0);
                        formData.append('name_2', this.form.receivers[1].name);
                        formData.append('td_2', this.form.receivers[1].td);
                        formData.append('nd_2', this.form.receivers[1].nd);
                        formData.append('bank_2', this.form.receivers[1].bank);
                        formData.append('branch_2', this.form.receivers[1].branch);
                        formData.append('tc_2', this.form.receivers[1].tc);
                        formData.append('nc_2', this.form.receivers[1].nc ? (this.form.receivers[1].nc).replace(/ /g, '') : null);
                        formData.append('phone_2', this.form.receivers[1].phone);
                        formData.append('img_2', this.form.receivers[1].img);
                        formData.append('amount_2', this.form.receivers[1].vSend);
                        formData.append('quotes_2', JSON.stringify(this.form.receivers[1]));
                        formData.append('fee_2', this.form.receivers[1].feeOut);
                        formData.append('fx_2', this.form.receivers[1].fx);
                        formData.append('convert_2', this.form.receivers[1].vReceiver);
                        formData.append('cConvert_2', this.form.receivers[1].cReceiver.code);
                        formData.append('destiny_2', this.form.receivers[1].cReceiver.country);
                    }

                    if (this.form.Qty > 2) {
                        formData.append('account_3', this.form.receivers[2].account ? this.form.receivers[2].account : 0);
                        formData.append('name_3', this.form.receivers[2].name);
                        formData.append('td_3', this.form.receivers[2].td);
                        formData.append('nd_3', this.form.receivers[2].nd);
                        formData.append('bank_3', this.form.receivers[2].bank);
                        formData.append('branch_3', this.form.receivers[2].branch);
                        formData.append('tc_3', this.form.receivers[2].tc);
                        formData.append('nc_3', this.form.receivers[2].nc ? (this.form.receivers[2].nc).replace(/ /g, '') : null);
                        formData.append('phone_3', this.form.receivers[2].phone);
                        formData.append('img_3', this.form.receivers[2].img);
                        formData.append('amount_3', this.form.receivers[2].vSend);
                        formData.append('quotes_3', JSON.stringify(this.form.receivers[2]));
                        formData.append('fee_3', this.form.receivers[2].feeOut);
                        formData.append('fx_3', this.form.receivers[2].fx);
                        formData.append('convert_3', this.form.receivers[2].vReceiver);
                        formData.append('cConvert_3', this.form.receivers[2].cReceiver.code);
                        formData.append('destiny_3', this.form.receivers[2].cReceiver.country);
                    }

                    if (this.form.Qty > 3) {
                        formData.append('account_4', this.form.receivers[3].account ? this.form.receivers[3].account : 0);
                        formData.append('name_4', this.form.receivers[3].name);
                        formData.append('td_4', this.form.receivers[3].td);
                        formData.append('nd_4', this.form.receivers[3].nd);
                        formData.append('bank_4', this.form.receivers[3].bank);
                        formData.append('branch_4', this.form.receivers[3].branch);
                        formData.append('tc_4', this.form.receivers[3].tc);
                        formData.append('nc_4', this.form.receivers[3].nc ? (this.form.receivers[3].nc).replace(/ /g, '') : null);
                        formData.append('phone_4', this.form.receivers[3].phone);
                        formData.append('img_4', this.form.receivers[3].img);
                        formData.append('amount_4', this.form.receivers[3].vSend);
                        formData.append('quotes_4', JSON.stringify(this.form.receivers[3]));
                        formData.append('fee_4', this.form.receivers[3].feeOut);
                        formData.append('fx_4', this.form.receivers[3].fx);
                        formData.append('convert_4', this.form.receivers[3].vReceiver);
                        formData.append('cConvert_4', this.form.receivers[3].cReceiver.code);
                        formData.append('destiny_4', this.form.receivers[3].cReceiver.country);
                    }

                    if (this.form.Qty > 4) {
                        formData.append('account_5', this.form.receivers[4].account ? this.form.receivers[4].account : 0);
                        formData.append('name_5', this.form.receivers[4].name);
                        formData.append('td_5', this.form.receivers[4].td);
                        formData.append('nd_5', this.form.receivers[4].nd);
                        formData.append('bank_5', this.form.receivers[4].bank);
                        formData.append('branch_5', this.form.receivers[4].branch);
                        formData.append('tc_5', this.form.receivers[4].tc);
                        formData.append('nc_5', this.form.receivers[4].nc ? (this.form.receivers[4].nc).replace(/ /g, '') : null);
                        formData.append('phone_5', this.form.receivers[4].phone);
                        formData.append('img_5', this.form.receivers[4].img);
                        formData.append('amount_5', this.form.receivers[4].vSend);
                        formData.append('quotes_5', JSON.stringify(this.form.receivers[4]));
                        formData.append('fee_5', this.form.receivers[4].feeOut);
                        formData.append('fx_5', this.form.receivers[4].fx);
                        formData.append('convert_5', this.form.receivers[4].vReceiver);
                        formData.append('cConvert_5', this.form.receivers[4].cReceiver.code);
                        formData.append('destiny_5', this.form.receivers[4].cReceiver.country);
                    }
                    this.appendSecurityPayload(formData, securityPayload);

                    axios.post('/api/request/save', formData, {
                        headers: {'Content-Type': 'multipart/form-data'}
                    }).then(res => {
                        this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                        this.$emit('confirm');
                    }).catch(err => {
                        this.handleErrors(err.response && err.response.data ? err.response.data.errors : null);
                        this.$toast.error(this.errorMessage(err, 'No fue posible crear la remesa.'), {icon: 'mdi-alert-circle'});
                    }).then(() => {
                        this.loading = false;
                    })
                }
            }
        }
    },
    destroyed() {
        clearTimeout(this.timeout);
        this.stopQuoteTimer();
    }
}
</script>

<style scoped>

</style>
